import React, { FC, useCallback, useMemo, useState } from 'react';
import {
	PaginationMetaInterface,
	ServiceProviderInterface,
	StudyRequestFilter,
	StudyRequestInterface,
	StudyRequestStatus,
} from '../interfaces';
import { convertToFormData } from '../utils';
import { useResponseHandlerService } from './response-handler.service';
import { useTranslation } from './translation.service';
import { useAuth0 } from '@auth0/auth0-react';

const apiUrl = process.env.REACT_APP_API_URL || '';
const serviceRootURL = `${apiUrl}/api/study-request`;
export const studyRequestAttachmentUrl = 'https://docs.google.com/document/d/1CLNs__vxyMtyirVjuUh7vWuKxxqg5CtBqmyhgg_iyVQ';
export const studyRequestAttachmentDescription = `${apiUrl}/downloads/description.pdf`;
const headers = {
	'Content-Type': 'application/json',
	Authorization: '',
};

interface StudyRequestServiceInterface {
	meta: PaginationMetaInterface;
	studyRequests: StudyRequestInterface[];
	createStudyRequest: (
		data: StudyRequestInterface
	) => Promise<Partial<StudyRequestInterface>>;
	createAdminStudyRequest: (
		data: StudyRequestInterface
	) => Promise<Partial<StudyRequestInterface>>;
	createStudyRequestBasic: (
		data: StudyRequestInterface
	) => Promise<Partial<StudyRequestInterface>>;
	getStudyRequests: (filter?: StudyRequestFilter) => Promise<void>;
	changeStudyRequestStatus: (
		requestId: string,
		status: StudyRequestStatus,
		notes?: string
	) => Promise<StudyRequestInterface>;
	getStudyRequestById: (id: string) => Promise<StudyRequestInterface>;
	updateStudyRequest: (id: string, data: StudyRequestInterface) => Promise<void>;
}

interface StudyRequestProviderInterface extends ServiceProviderInterface {}

const { createContext, useContext } = React;

const StudyRequestContext = createContext<StudyRequestServiceInterface>(
	{} as StudyRequestServiceInterface
);

export const StudyRequestProvider: FC<StudyRequestProviderInterface> = (props) => {
	const { getIdTokenClaims } = useAuth0();

	const { addSuccess } = useResponseHandlerService();
	const { t } = useTranslation();
	const [studyRequests, setStudyRequests] = useState<StudyRequestInterface[]>([]);
	const [meta, setMeta] = useState({} as any);

	const getStudyRequests = useCallback(
		async (filter?: StudyRequestFilter) => {
			const user = await getIdTokenClaims();
			headers.Authorization = user?.__raw || '';
			const res = await getStudyRequestsApiCall(filter);
			if (res) {
				setStudyRequests(res.items);
				setMeta(res.meta);
			}
		},
		[setStudyRequests, getIdTokenClaims]
	);

	const changeStudyRequestStatus = useCallback(
		async (requestId: string, status: StudyRequestStatus, notes?: string) => {
			const user = await getIdTokenClaims();
			headers.Authorization = user?.__raw || '';
			const res = await changeStudyRequestStatusApiCall(requestId, status, notes);
			if (res) {
				addSuccess(t('Study Request status was successfully changed.'));
				const index = studyRequests.findIndex(
					(studyReq) => studyReq.id === res.id
				);
				const updatedStudyRequests = [...studyRequests];
				updatedStudyRequests[index] = res;
				setStudyRequests(updatedStudyRequests);
			}
			return res;
		},
		[addSuccess, t, studyRequests, getIdTokenClaims]
	);

	const updateStudyRequest = useCallback(
		async (id: string, data: StudyRequestInterface) => {
			const user = await getIdTokenClaims();
			headers.Authorization = user?.__raw || '';
			data.status = StudyRequestStatus.PENDING;
			const res = await updateStudyRequestApiCall(id, data);
			if (res) {
				addSuccess(t('Study Request was successfully updated.'));
				const index = studyRequests.findIndex(
					(studyReq) => studyReq.id === res.id
				);
				const updatedStudyRequests = [...studyRequests];
				updatedStudyRequests[index] = res;
				setStudyRequests(updatedStudyRequests);
			}
		},
		[getIdTokenClaims, addSuccess, t, studyRequests]
	);

	const createStudyRequest = useCallback( async (
		requestData: StudyRequestInterface
	): Promise<Partial<StudyRequestInterface>> => {
		const user = await getIdTokenClaims();
		headers.Authorization = user?.__raw || '';
		const res = await createStudyRequestApiCall(requestData);
		return res;
	},[getIdTokenClaims]);

	const createAdminStudyRequest = useCallback( async (
		requestData: StudyRequestInterface
	): Promise<Partial<StudyRequestInterface>> => {
		const user = await getIdTokenClaims();
		headers.Authorization = user?.__raw || '';
		const res = await createAdminStudyRequestApiCall(requestData);
		return res;
	},[getIdTokenClaims]);

	const createStudyRequestBasic = useCallback( async (
		requestData: StudyRequestInterface
	): Promise<Partial<StudyRequestInterface>> => {
		const user = await getIdTokenClaims();
		headers.Authorization = user?.__raw || '';
		const res = await createStudyRequestBasicApiCall(requestData);
		return res;
	},[getIdTokenClaims]);

	const value = useMemo(
		() => ({
			meta,
			getStudyRequests,
			studyRequests,
			createStudyRequest,
			createStudyRequestBasic,
			createAdminStudyRequest,
			changeStudyRequestStatus,
			getStudyRequestById,
			updateStudyRequest,
		}),
		[studyRequests, meta]
	);

	return (
		<StudyRequestContext.Provider value={value}>
			{props.children}
		</StudyRequestContext.Provider>
	);
};

export const useStudyRequestService = () => {
	return useContext<StudyRequestServiceInterface>(StudyRequestContext);
};

const createStudyRequestApiCall = async (
	requestData: StudyRequestInterface
): Promise<Partial<StudyRequestInterface>> => {
	const formData = convertToFormData(requestData);
	try {
		const res = await window.fetch(serviceRootURL, {
			method: 'POST',
			body: formData,
			headers: {
				Authorization: headers.Authorization,
			},
		});
		return res.json();
	} catch (e) {
		console.error(e);
		throw e;
	}
};

const createAdminStudyRequestApiCall = async (
	requestData: StudyRequestInterface
): Promise<Partial<StudyRequestInterface>> => {
	const formData = convertToFormData(requestData);
	try {
		const res = await window.fetch(`${serviceRootURL}/admin`, {
			method: 'POST',
			body: formData,
			headers: {
				Authorization: headers.Authorization,
			},
		});
		return res.json();
	} catch (e) {
		console.error(e);
		throw e;
	}
};

const createStudyRequestBasicApiCall = async (
	requestData: StudyRequestInterface
): Promise<Partial<StudyRequestInterface>> => {
	try {
		const {requestAttachments, ...rest} = requestData;
		const res = await window.fetch(`${serviceRootURL}/basic`, {
			method: 'POST',
			body: JSON.stringify(rest),
			headers,
		});
		return res.json();
	} catch (e) {
		console.error(e);
		throw e;
	}
};

const getStudyRequestsApiCall = async (
	filter?: any
): Promise<{ items: StudyRequestInterface[]; meta: PaginationMetaInterface }> => {
	const params = new URLSearchParams(filter).toString();
	const res = await window.fetch(`${serviceRootURL}?${params}`, { headers });
	return res.json();
};

const changeStudyRequestStatusApiCall = async (
	requestId: string,
	status: StudyRequestStatus,
	notes?: string
): Promise<StudyRequestInterface> => {
	const res = await window.fetch(`${serviceRootURL}/${requestId}/status`, {
		method: 'PATCH',
		body: JSON.stringify({ status, notes }),
		headers,
	});
	return res.json();
};

const getStudyRequestById = async (id: string): Promise<StudyRequestInterface> => {
	const res = await window.fetch(`${serviceRootURL}/${id}`, { headers });
	return res.json();
};

const updateStudyRequestApiCall = async (
	id: string,
	requestData: StudyRequestInterface
): Promise<StudyRequestInterface> => {
	try {
		const { students, requestAttachments, ...rest } = requestData;
		const res = await window.fetch(`${serviceRootURL}/${id}`, {
			method: 'PUT',
			body: JSON.stringify(rest),
			headers,
		});
		return res.json();
	} catch (e) {
		console.error(e);
		throw e;
	}
};
